import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CER_BLOCK_START,
  DASHBOARD_JOB_STATUS_LABELS,
  geolocatedAddressHint,
  groupJobsIntoVisits,
  visitOpenJob,
  type Client,
  type InspectionJob,
  type JobOverviewFilter,
  type JobsOverview,
  type JobOverviewType,
  type JobStatus,
  type DashboardJobStatus,
  type JobMapPoint,
  type JobVisit,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  fetchClients,
  fetchJobsMapBaseUrl,
  fetchJobsOverview,
  fetchJobsMapPoints,
  geocodePendingJobs,
  uploadIntakeFile,
  type IntakeCsvResult,
} from '../api.js';
import { jobStatusLabel } from '../format.js';
import { getSession } from '../session.js';
import { JobLocationsMap } from '../components/JobLocationsMap.js';
import { Icon, type IconName } from '../components/Icon.js';
import { Kpi } from '../components/Kpi.js';
import { FilterSelect } from '../components/FilterSelect.js';
import { percentage, trendDelta, type Trend } from '../components/trend.js';
import {
  BORDERS,
  MAINLAND,
  MAP_FRAME,
  STATE_CITY,
  STATE_LABELS,
  TASMANIA,
  projectToFrame,
  toPath,
} from '../australia-map.js';
import { createServiceM8Jobs, pushServiceM8Updates } from '../api.js';
import type { ServiceM8PushResult } from '../api.js';

type Filters = {
  search: string;
  clientId: string;
  trancheId: string;
  state: string;
  area: string;
  type: '' | JobOverviewType;
  status: '' | JobStatus;
  dashboardStatus: '' | DashboardJobStatus;
  inspectorId: string;
  fromDate: string;
  toDate: string;
  rollup: '' | 'allocated' | 'submitted' | 'pending';
};
const EMPTY: Filters = {
  search: '',
  clientId: '',
  trancheId: '',
  state: '',
  area: '',
  type: '',
  status: '',
  dashboardStatus: '',
  inspectorId: '',
  fromDate: '',
  toDate: '',
  rollup: '',
};
type SavedView = { name: string; filters: Filters };

/**
 * Groups per create request. At ~1.5s a group against a 30s Lambda, ten leaves
 * roughly half the budget spare for a slow ServiceM8 response — enough margin
 * that a batch does not sit near the ceiling, small enough that a lost batch is
 * cheap to redo.
 */
const SM8_CREATE_BATCH_SIZE = 10;
/**
 * Jobs per push-updates request. Larger than the create batch because an
 * update is a single ServiceM8 write per job rather than the six calls a
 * create costs, but still bounded so a run stays inside the Lambda's 30s.
 */
const SM8_PUSH_BATCH_SIZE = 25;

export function JobsListScreen() {
  const userKey = getSession()?.email ?? getSession()?.name ?? 'local';
  const storageKey = `cer-jobs-views:${userKey}`;
  const filterStorageKey = `cer-jobs-filters:${userKey}`;
  const [filters, setFilters] = useState<Filters>(() => {
    try {
      return {
        ...EMPTY,
        ...(JSON.parse(localStorage.getItem(filterStorageKey) ?? '{}') as Partial<Filters>),
      };
    } catch {
      return EMPTY;
    }
  });
  const [overview, setOverview] = useState<JobsOverview | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadClientId, setUploadClientId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<IntakeCsvResult | null>(null);
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) ?? '[]') as SavedView[];
    } catch {
      return [];
    }
  });
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [sm8Creating, setSm8Creating] = useState(false);
  const [sm8Result, setSm8Result] = useState<{
    created: Array<{ jobIds: string[]; serviceM8JobNumber: string }>;
    skipped: Array<{ jobIds: string[]; reason: string }>;
  } | null>(null);
  const [sm8Error, setSm8Error] = useState<string | null>(null);
  /** Work orders raised so far, so a long run isn't a frozen button. */
  const [sm8Progress, setSm8Progress] = useState<{ done: number; total: number } | null>(null);

  /**
   * Pushing corrections onto work orders that already exist. Held separately
   * from the create-jobs state above: they run from different buttons and a
   * previous create result must stay on screen while this one runs.
   */
  const [pushBusy, setPushBusy] = useState<'preview' | 'apply' | null>(null);
  const [pushProgress, setPushProgress] = useState<{ done: number; total: number } | null>(null);
  const [pushResult, setPushResult] = useState<(ServiceM8PushResult & { applied: boolean }) | null>(
    null,
  );
  const [pushError, setPushError] = useState<string | null>(null);

  /**
   * Which combined visits have their second inspection disclosed. Collapsed by
   * default: the list answers "how many site trips are there", and the second
   * code is detail you open when you want it.
   */
  const [openVisits, setOpenVisits] = useState<ReadonlySet<string>>(new Set());
  /**
   * One row per SITE TRIP. The feed is one row per accreditation code, so a
   * combined PV+battery job arrived as two records at one address and the grid
   * listed the same house twice, once from each half's point of view — which
   * reads as duplicated data. The server pages by visit, so a pair is never
   * split across the page boundary and this regrouping is always complete.
   */
  const visits = useMemo(() => groupJobsIntoVisits(overview?.jobs ?? []), [overview]);

  const [geocodeState, setGeocodeState] = useState({
    running: false,
    done: false,
    matched: 0,
    failed: 0,
    remaining: 0,
  });
  const [mapPoints, setMapPoints] = useState<{ points: JobMapPoint[]; ungeocoded: number }>({
    points: [],
    ungeocoded: 0,
  });

  // Debounce the same way Reports does — search especially fires many
  // onChange calls, and the overview refetch is not cheap.
  const debouncedFilters = useDebounced(filters, 200);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filter = Object.fromEntries(
        Object.entries(debouncedFilters).filter(([, value]) => value),
      ) as JobOverviewFilter;
      const [next, points] = await Promise.all([
        fetchJobsOverview(filter),
        // The map's own source: every matching job with a coordinate, not the
        // overview's first 500. A failure here leaves the map empty rather
        // than taking the whole dashboard down.
        fetchJobsMapPoints(filter).catch(() => ({ points: [], ungeocoded: 0 })),
      ]);
      setOverview(next);
      setMapPoints(points);
      setRefreshedAt(new Date());
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    localStorage.setItem(filterStorageKey, JSON.stringify(filters));
  }, [filterStorageKey, filters]);
  useEffect(() => {
    void fetchClients(true)
      .then(setClients)
      .catch((err: unknown) => setError(apiErrorReason(err)));
  }, []);

  function update(patch: Partial<Filters>) {
    setFilters((current) => ({ ...current, ...patch }));
  }
  function toggleVisit(visitId: string) {
    setOpenVisits((current) => {
      const next = new Set(current);
      if (!next.delete(visitId)) next.add(visitId);
      return next;
    });
  }
  function applyNumber(clientId: string, rollup: Filters['rollup'], type: Filters['type'] = '') {
    update({ clientId, rollup, type, status: '', dashboardStatus: '' });
    document.getElementById('jobs-grid')?.scrollIntoView({ behavior: 'smooth' });
  }
  function saveView() {
    const name = viewName.trim();
    if (!name) return;
    const next = [...savedViews.filter((view) => view.name !== name), { name, filters }];
    setSavedViews(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
    setSaveViewOpen(false);
    setViewName('');
  }
  async function upload(file: File) {
    setUploading(true);
    try {
      setUploadResult(await uploadIntakeFile(file, uploadClientId || undefined));
      await load();
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  /**
   * Drive the deferred geocoding to completion. Each call is bounded server-side
   * so it can't time out; we keep calling until nothing is left, and stop early
   * if a chunk makes no progress so a batch of unmatchable addresses can't spin
   * forever.
   */
  /**
   * Raise every recommended work order, a batch of requests at a time. Only the
   * create kinds are eligible: amend and split mutate a work order someone
   * already relies on, so they are approved per line instead.
   *
   * One request for the whole run cannot finish. Each group costs about six
   * sequential ServiceM8 calls — two duplicate lookups, then create, contact,
   * note and reload — so roughly 1.5s, against a backend Lambda capped at 30
   * seconds. A 169-group run on 2026-08-12 created 19 work orders in 28s and
   * was then killed mid-loop: the browser got a 504, and because no response
   * ever arrived it showed no record of the 19 that HAD been raised. Worse, the
   * on-screen recommendations were then stale, so pressing the button again
   * would have re-created the combos among them — `findJobByPurchaseOrderNumber`
   * matches a purchase order exactly and cannot see a code inside a combo's
   * "PVD… / BTY…".
   *
   * Batching keeps every request well inside the Lambda's budget, and makes a
   * failure cost one batch rather than the run. Results accumulate as they land
   * so a partial run is still reported. Sequential by design: the point is to
   * stay under a time and concurrency ceiling, not to race it.
   */
  async function createRecommendedServiceM8Jobs(): Promise<void> {
    if (!uploadResult) return;
    const groups = uploadResult.serviceM8.recommendations
      .filter((rec) => rec.kind === 'create-combo' || rec.kind === 'create-single')
      .map((rec) => rec.jobIds);
    if (groups.length === 0) return;
    setSm8Creating(true);
    setSm8Error(null);
    setSm8Result(null);

    const created: Array<{ jobIds: string[]; serviceM8JobNumber: string }> = [];
    const skipped: Array<{ jobIds: string[]; reason: string }> = [];
    try {
      for (let index = 0; index < groups.length; index += SM8_CREATE_BATCH_SIZE) {
        setSm8Progress({ done: index, total: groups.length });
        const batch = groups.slice(index, index + SM8_CREATE_BATCH_SIZE);
        try {
          const result = await createServiceM8Jobs(batch);
          created.push(...result.created);
          skipped.push(...result.skipped);
          // Published per batch, so a later failure still leaves the operator
          // holding the numbers that were raised before it.
          setSm8Result({ created: [...created], skipped: [...skipped] });
        } catch (err) {
          setSm8Error(
            `${err instanceof Error ? err.message : 'ServiceM8 create failed'} — stopped after ${created.length} work order${created.length === 1 ? '' : 's'}. Re-upload the file before trying again: this batch may have been partly written, and the recommendations on screen are now out of date.`,
          );
          break;
        }
      }
    } finally {
      setSm8Progress(null);
      setSm8Creating(false);
      await load();
    }
  }

  /**
   * Jobs on screen that ServiceM8 already holds a work order for.
   *
   * Scoped to what is loaded rather than to the whole filter: the overview
   * returns only the first page, so promising to update "all matching jobs"
   * would quietly cover a subset. The button says how many it will touch.
   */
  const pushableJobIds = useMemo(
    () =>
      (overview?.jobs ?? [])
        .filter((job) => Boolean(job.serviceM8JobNumber))
        .map((job) => job.jobId),
    [overview],
  );

  /**
   * How many of the pending changes keep text somebody else wrote.
   *
   * A push used to REPLACE the booking note, and on most live work orders that
   * meant deleting access notes and booking instructions the office had typed
   * — content no re-import can bring back. It now splices our block in and
   * keeps the rest, and the delimiters in the new text are what say so. Said
   * out loud in the summary because "357 of 357 would change" is exactly the
   * sentence someone approves without expanding the diff.
   */
  const preservingNotes = useMemo(
    () =>
      (pushResult?.updates ?? []).filter((update) =>
        update.changes.some(
          (change) => change.field === 'job_description' && change.to.includes(CER_BLOCK_START),
        ),
      ).length,
    [pushResult],
  );

  /**
   * Preview, then apply, the corrections for those jobs.
   *
   * Two passes over the same endpoint: `confirm: false` writes nothing and
   * returns the diff, and the operator sees it before anything reaches a
   * booking note an inspector is working from.
   *
   * Batched for the same reason creates are — each job costs a ServiceM8 write
   * and the backend Lambda stops at 30 seconds — and results accumulate as
   * batches land so a failure part-way still reports what it did.
   */
  async function runPushUpdates(confirm: boolean): Promise<void> {
    if (pushableJobIds.length === 0) return;
    // Applying is driven by the jobs the preview actually flagged, so an
    // operator can never confirm more than the diff they were shown.
    const ids = confirm
      ? (pushResult?.updates.map((update) => update.jobId) ?? [])
      : pushableJobIds;
    if (ids.length === 0) return;

    setPushBusy(confirm ? 'apply' : 'preview');
    setPushError(null);
    if (!confirm) setPushResult(null);

    const updates: ServiceM8PushResult['updates'] = [];
    const skipped: ServiceM8PushResult['skipped'] = [];
    try {
      for (let index = 0; index < ids.length; index += SM8_PUSH_BATCH_SIZE) {
        setPushProgress({ done: index, total: ids.length });
        const batch = ids.slice(index, index + SM8_PUSH_BATCH_SIZE);
        try {
          const result = await pushServiceM8Updates(batch, confirm);
          updates.push(...result.updates);
          skipped.push(...result.skipped);
          setPushResult({ updates: [...updates], skipped: [...skipped], applied: confirm });
        } catch (err) {
          setPushError(
            `${err instanceof Error ? err.message : 'ServiceM8 update failed'}${
              confirm
                ? ` — stopped after ${updates.length} update${updates.length === 1 ? '' : 's'}. The rest were not written; re-run to continue.`
                : ''
            }`,
          );
          break;
        }
      }
    } finally {
      setPushProgress(null);
      setPushBusy(null);
      if (confirm) await load();
    }
  }

  async function runGeocoding() {
    setGeocodeState({ running: true, done: false, matched: 0, failed: 0, remaining: 0 });
    let matched = 0;
    let failed = 0;
    try {
      for (;;) {
        const chunk = await geocodePendingJobs();
        matched += chunk.matched;
        failed += chunk.failed;
        setGeocodeState({
          running: true,
          done: false,
          matched,
          failed,
          remaining: chunk.remaining,
        });
        // `processed === 0` guards against a chunk that can make no progress.
        if (chunk.remaining === 0 || chunk.processed === 0) break;
      }
      setGeocodeState({ running: false, done: true, matched, failed, remaining: 0 });
      await load();
    } catch (err) {
      setGeocodeState({ running: false, done: false, matched, failed, remaining: 0 });
      setError(apiErrorReason(err));
    }
  }

  /** Header chip: when the overview on screen was fetched. */
  const refreshedLabel = refreshedAt
    ? `${refreshedAt.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })} ${refreshedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : 'Loading…';

  return (
    <main className="screen jobs-overview">
      <div className="screen__header">
        <div>
          <h2>Jobs Overview</h2>
          <p className="subtitle">Allocation, submission and milestone overview</p>
        </div>
        <div className="cases-upload">
          <select
            aria-label="CSV client"
            value={uploadClientId}
            onChange={(e) => setUploadClientId(e.target.value)}
          >
            <option value="">Auto-detect client</option>
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId}>
                {client.name}
              </option>
            ))}
          </select>
          <button
            className="primary"
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Upload CSV / Excel'}
          </button>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <div className="refresh-chip">
            <Icon name="calendar" />
            <span>{refreshedLabel}</span>
            <button
              type="button"
              aria-label="Refresh dashboard"
              disabled={loading}
              onClick={() => void load()}
            >
              <Icon name="refresh" />
            </button>
          </div>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {uploadResult && (
        <section className="intake-result">
          <p className="banner banner--success">
            Imported {uploadResult.created} jobs from{' '}
            {uploadResult.sourceFormat === 'xlsx' ? 'Excel' : 'CSV'} for{' '}
            {clients.find((client) => client.clientId === uploadResult.clientId)?.name ??
              uploadResult.clientId}
            ; {uploadResult.duplicates} dashboard duplicates.{' '}
            {uploadResult.serviceM8.matched > 0 && (
              <>Linked {uploadResult.serviceM8.matched} to existing ServiceM8 jobs. </>
            )}
            Nothing was sent to ServiceM8.
          </p>

          {/* What ServiceM8 needs, one row per SITE rather than per job. The
              entry that matters is create-combo: a combined PV+battery visit
              is ONE work order in ServiceM8, and raising two would double-book
              the inspector for a single trip. */}
          {uploadResult.serviceM8.recommendations.some(
            (rec) => rec.kind === 'create-combo' || rec.kind === 'create-single',
          ) && (
            <div className="geocode-prompt">
              <span>
                {
                  uploadResult.serviceM8.recommendations.filter(
                    (rec) => rec.kind === 'create-combo' || rec.kind === 'create-single',
                  ).length
                }{' '}
                work order
                {uploadResult.serviceM8.recommendations.filter(
                  (rec) => rec.kind === 'create-combo' || rec.kind === 'create-single',
                ).length === 1
                  ? ''
                  : 's'}{' '}
                to raise in ServiceM8. Combos are created as a single job carrying both codes.
              </span>
              <button
                type="button"
                className="dark-btn"
                disabled={sm8Creating}
                onClick={() => void createRecommendedServiceM8Jobs()}
              >
                {sm8Creating
                  ? sm8Progress
                    ? `Creating… ${sm8Progress.done} of ${sm8Progress.total}`
                    : 'Creating…'
                  : 'Create in ServiceM8'}
              </button>
            </div>
          )}
          {sm8Error && <p className="banner banner--error">{sm8Error}</p>}
          {sm8Result && (
            <p className={`banner banner--${sm8Result.skipped.length > 0 ? 'warning' : 'success'}`}>
              Created {sm8Result.created.length} ServiceM8 job
              {sm8Result.created.length === 1 ? '' : 's'}
              {sm8Result.created.length > 0 &&
                ` (#${sm8Result.created.map((c) => c.serviceM8JobNumber).join(', #')})`}
              {sm8Result.skipped.length > 0 &&
                `; ${sm8Result.skipped.length} skipped — ${sm8Result.skipped
                  .slice(0, 3)
                  .map((s) => `${s.jobIds.join('+')}: ${s.reason}`)
                  .join('; ')}`}
              .
            </p>
          )}

          {uploadResult.serviceM8.recommendations.length > 0 && (
            <details className="sm8-preview" open>
              <summary>
                ServiceM8 actions needed
                {uploadResult.serviceM8.combosDetected > 0 &&
                  ` · ${uploadResult.serviceM8.combosDetected} combo${
                    uploadResult.serviceM8.combosDetected === 1 ? '' : 's'
                  } detected`}
              </summary>
              <div className="sm8-preview__table">
                <div className="sm8-preview__row sm8-preview__row--header">
                  <span>Action</span>
                  <span>Accreditation codes</span>
                  <span>Detail</span>
                </div>
                {uploadResult.serviceM8.recommendations.map((rec) => (
                  <div className="sm8-preview__row" key={rec.jobIds.join('+')}>
                    <span className={`sm8-preview__status sm8-preview__status--${rec.kind}`}>
                      {rec.kind === 'linked'
                        ? 'Already linked'
                        : rec.kind === 'create-combo'
                          ? 'Create ONE combined job'
                          : rec.kind === 'create-single'
                            ? 'Create job'
                            : rec.kind === 'amend-to-combo'
                              ? 'Amend existing job'
                              : 'Split across two jobs'}
                    </span>
                    <span>
                      <strong>{rec.jobIds.join(' + ')}</strong>
                    </span>
                    <span>
                      {rec.kind === 'linked' && `ServiceM8 #${rec.serviceM8JobNumber}`}
                      {rec.kind === 'create-combo' &&
                        `One work order, purchase order "${rec.purchaseOrderNumber}"`}
                      {rec.kind === 'create-single' &&
                        `Purchase order "${rec.purchaseOrderNumber}"`}

                      {/* Amend and split change a work order someone is already
                          relying on, so each is approved on its own line after
                          reading the diff — never swept along by the bulk
                          create. Both are disabled until the update path
                          ships; the diff is shown now so the decision is
                          reviewable in the meantime. */}
                      {rec.kind === 'amend-to-combo' && (
                        <details className="sm8-diff">
                          <summary>
                            #{rec.serviceM8JobNumber} carries only one code — review change
                          </summary>
                          <dl className="sm8-diff__body">
                            <dt>Purchase order in ServiceM8</dt>
                            <dd>{rec.jobIds.find((id) => id) ?? '—'} (one code only)</dd>
                            <dt>Proposed</dt>
                            <dd>{rec.purchaseOrderNumber}</dd>
                          </dl>
                          <button type="button" className="dark-btn" disabled>
                            Approve amend (not yet available)
                          </button>
                        </details>
                      )}
                      {rec.kind === 'split-across-jobs' && (
                        <details className="sm8-diff">
                          <summary>
                            Split across #{rec.serviceM8JobNumbers.join(' and #')} — review
                          </summary>
                          <dl className="sm8-diff__body">
                            <dt>In ServiceM8</dt>
                            <dd>
                              Two separate work orders, one per code (#
                              {rec.serviceM8JobNumbers.join(', #')})
                            </dd>
                            <dt>Proposed</dt>
                            <dd>
                              One combined work order for {rec.jobIds.join(' + ')} — merging is a
                              manual step in ServiceM8
                            </dd>
                          </dl>
                          <button type="button" className="dark-btn" disabled>
                            Approve merge (not yet available)
                          </button>
                        </details>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {uploadResult.serviceM8.ambiguousSites.length > 0 && (
            <p className="banner banner--warning">
              {uploadResult.serviceM8.ambiguousSites.length} site
              {uploadResult.serviceM8.ambiguousSites.length === 1 ? '' : 's'} could not be paired
              automatically — more than two rows, or two of the same type, share one address. Left
              unlinked rather than guessed:{' '}
              {uploadResult.serviceM8.ambiguousSites.slice(0, 3).join('; ')}
              {uploadResult.serviceM8.ambiguousSites.length > 3 && ' …'}
            </p>
          )}

          {uploadResult.serviceM8.clientCollisions.length > 0 && (
            <p className="banner banner--error">
              {uploadResult.serviceM8.clientCollisions.length} client collision
              {uploadResult.serviceM8.clientCollisions.length === 1 ? '' : 's'} — rows from
              different customers share an address, or a code matched a ServiceM8 job owned by
              another customer. Not paired and not linked, because attributing an inspection to the
              wrong customer is worse than leaving it for a human:{' '}
              {uploadResult.serviceM8.clientCollisions.slice(0, 3).join('; ')}
              {uploadResult.serviceM8.clientCollisions.length > 3 && ' …'}
            </p>
          )}

          {uploadResult.serviceM8.duplicateCodes.length > 0 && (
            <p className="banner banner--warning">
              {uploadResult.serviceM8.duplicateCodes.length} accreditation code
              {uploadResult.serviceM8.duplicateCodes.length === 1 ? ' is' : 's are'} claimed by more
              than one ServiceM8 job, so the link picks whichever ServiceM8 returned last. Worth
              reconciling: {uploadResult.serviceM8.duplicateCodes.slice(0, 5).join(', ')}
              {uploadResult.serviceM8.duplicateCodes.length > 5 && ' …'}
            </p>
          )}
          {uploadResult.geocoding.deferred > 0 && (
            <div className="geocode-prompt">
              <span>
                {geocodeState.running
                  ? `Geocoding addresses… ${geocodeState.remaining.toLocaleString()} to go`
                  : geocodeState.done
                    ? `Geocoded ${geocodeState.matched.toLocaleString()} addresses${geocodeState.failed ? `; ${geocodeState.failed.toLocaleString()} could not be matched` : ''}.`
                    : `${uploadResult.geocoding.deferred.toLocaleString()} new addresses need coordinates before they appear on the map.`}
              </span>
              {!geocodeState.running && !geocodeState.done && (
                <button type="button" className="dark-btn" onClick={() => void runGeocoding()}>
                  Geocode now
                </button>
              )}
            </div>
          )}
          {uploadResult.serviceM8.drafts.length > 0 && (
            <details className="sm8-preview" open>
              <summary>ServiceM8 write preview</summary>
              <p className="muted">
                Read-only duplicate checks are live. Creating jobs remains disabled.
              </p>
              <div className="sm8-preview__table">
                <div className="sm8-preview__row sm8-preview__row--header">
                  <span>Result</span>
                  <span>Job</span>
                  <span>Site</span>
                  <span>Contact</span>
                </div>
                {uploadResult.serviceM8.drafts.map(({ action, draft, existingJobNumber }) => (
                  <div className="sm8-preview__row" key={draft.job.purchase_order_number}>
                    <span className={`sm8-preview__status sm8-preview__status--${action}`}>
                      {action === 'already-exists'
                        ? `Already exists${existingJobNumber ? ` · #${existingJobNumber}` : ''}`
                        : action === 'would-create'
                          ? 'Would create'
                          : action === 'configuration-required'
                            ? 'Needs configuration'
                            : 'Not checked'}
                    </span>
                    <span>
                      <strong>{draft.job.purchase_order_number}</strong>
                      <small>{draft.job.status} · Inspections</small>
                    </span>
                    <span>
                      {draft.job.job_address}
                      <small>Bill to: {draft.job.billing_address}</small>
                    </span>
                    <span>
                      {[draft.siteContact.first, draft.siteContact.last].filter(Boolean).join(' ')}
                      <small>{draft.siteContact.mobile || draft.siteContact.email}</small>
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      )}
      <section className="overview-kpis" aria-label="Job KPIs">
        <Kpi
          label="Allocated"
          value={overview?.kpis.allocated}
          unit="Jobs"
          tone="blue"
          icon="clipboard"
          percent={percentage(
            overview?.kpis.allocated,
            overview ? overview.kpis.allocated + overview.kpis.stillToCome : undefined,
          )}
          percentCaption="of expected"
          spark={findTrend(overview, 'allocated')}
          onClick={() => applyNumber('', 'allocated')}
        />
        <Kpi
          label="Submitted"
          value={overview?.kpis.submitted}
          unit="Jobs"
          tone="green"
          icon="send"
          percent={percentage(overview?.kpis.submitted, overview?.kpis.allocated)}
          percentCaption="of allocated"
          trend={findTrend(overview, 'submitted')}
          onClick={() => applyNumber('', 'submitted')}
        />
        <Kpi
          label="Pending Submission"
          value={overview?.kpis.pendingSubmission}
          unit="Jobs"
          tone="amber"
          icon="clock"
          percent={percentage(overview?.kpis.pendingSubmission, overview?.kpis.allocated)}
          percentCaption="of allocated"
          footnote={
            overview?.kpis.oldestOutstandingDays
              ? { text: `Oldest ${overview.kpis.oldestOutstandingDays} days`, tone: 'warn' }
              : undefined
          }
          onClick={() => applyNumber('', 'pending')}
        />
        <Kpi
          label="Risks"
          value={overview?.kpis.atRisk}
          unit={overview?.kpis.atRisk === 1 ? 'Tranche' : 'Tranches'}
          tone="red"
          icon="alert"
          footnote={
            overview
              ? overview.kpis.atRisk
                ? { text: 'Needs attention', tone: 'risk' }
                : { text: 'All on track', tone: 'good' }
              : undefined
          }
          onClick={() => {
            document.getElementById('milestones')?.scrollIntoView({ behavior: 'smooth' });
          }}
        />
        <Kpi
          label="Scheduled"
          value={overview?.kpis.scheduled}
          unit="Jobs"
          tone="violet"
          icon="calendar"
          percent={percentage(overview?.kpis.scheduled, overview?.kpis.allocated)}
          percentCaption="of allocated"
          trend={findTrend(overview, 'booked')}
        />
        <Kpi
          label="Inspection Complete"
          value={overview?.kpis.inspectionCompleteReportOutstanding}
          unit="Reports"
          tone="green"
          icon="check"
          percent={percentage(
            overview?.kpis.inspectionCompleteReportOutstanding,
            overview?.kpis.allocated,
          )}
          percentCaption="of allocated"
          trend={findTrend(overview, 'completed')}
        />
      </section>
      <section
        className="overview-band overview-filters overview-filters--jobs"
        aria-label="Job filters"
      >
        <div className="filter-grids">
          <div className="filter-grid filter-grid--jobs-primary">
            <label>
              <span>Search</span>
              <input
                type="search"
                value={filters.search}
                placeholder="Job, SM8 #, homeowner or address"
                onChange={(e) => update({ search: e.target.value })}
              />
            </label>
            <FilterSelect
              label="Client"
              value={filters.clientId}
              onChange={(value) => update({ clientId: value })}
              options={clients.map((client) => [client.clientId, client.name])}
            />
            <FilterSelect
              label="Tranche"
              value={filters.trancheId}
              onChange={(value) => update({ trancheId: value })}
              options={(overview?.options.tranches ?? []).map((v) => [v, v])}
            />
            <FilterSelect
              label="State"
              value={filters.state}
              onChange={(value) => update({ state: value })}
              options={(overview?.options.states ?? []).map((v) => [v, v])}
            />
            <FilterSelect
              label="Area"
              value={filters.area}
              onChange={(value) => update({ area: value })}
              options={(overview?.options.areas ?? []).map((v) => [v, v])}
            />
            <FilterSelect
              label="Type"
              value={filters.type}
              onChange={(value) => update({ type: value as Filters['type'] })}
              options={[
                ['pvd', 'PVD'],
                ['bty', 'BTY'],
                ['both', 'PVD+BTY'],
              ]}
            />
            <FilterSelect
              label="Status"
              value={filters.dashboardStatus}
              onChange={(value) =>
                update({
                  dashboardStatus: value as Filters['dashboardStatus'],
                  status: '',
                  rollup: '',
                })
              }
              options={Object.entries(DASHBOARD_JOB_STATUS_LABELS)}
            />
          </div>
          <div className="filter-grid filter-grid--jobs-secondary">
            {/* The roll-up used to be invisible: the KPI tiles and the client
                table set it, it persisted to localStorage with the rest of the
                filters, and nothing on screen said it was on. A single click on
                "Allocated" months ago then quietly hid every cancelled job — and
                the grid still reported its count as the whole book. Given that
                count is what a bulk ServiceM8 create gets sanity-checked against,
                the filter has to be visible and clearable like any other. */}
            <FilterSelect
              label="Roll-up"
              value={filters.rollup}
              onChange={(value) =>
                update({ rollup: value as Filters['rollup'], status: '', dashboardStatus: '' })
              }
              options={[
                ['allocated', 'Allocated'],
                ['submitted', 'Submitted'],
                ['pending', 'Pending submission'],
              ]}
              allLabel="All jobs"
            />
            <FilterSelect
              label="Inspector"
              value={filters.inspectorId}
              onChange={(value) => update({ inspectorId: value })}
              options={(overview?.options.inspectors ?? []).map((v) => [v, v])}
            />
            <label>
              <span>From</span>
              <input
                type="date"
                value={filters.fromDate}
                onChange={(e) => update({ fromDate: e.target.value })}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={filters.toDate}
                onChange={(e) => update({ toDate: e.target.value })}
              />
            </label>
          </div>
        </div>
        <div className="filter-actions">
          <button type="button" className="ghost-btn" onClick={() => setFilters(EMPTY)}>
            Clear
          </button>
          <div className="saved-views">
            <button type="button" className="dark-btn" onClick={() => setSaveViewOpen(true)}>
              Save view
            </button>
            <button
              type="button"
              className="dark-btn dark-btn--caret"
              aria-label="Saved views"
              aria-expanded={viewMenuOpen}
              onClick={() => setViewMenuOpen((open) => !open)}
            >
              <Icon name="caret" />
            </button>
            {viewMenuOpen && (
              <ul className="saved-views__menu">
                {savedViews.length === 0 && <li className="saved-views__empty">No saved views</li>}
                {savedViews.map((view) => (
                  <li key={view.name}>
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ ...EMPTY, ...view.filters });
                        setViewMenuOpen(false);
                      }}
                    >
                      {view.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
      {saveViewOpen && (
        <div className="modal">
          <div
            className="modal__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="save-view-title"
          >
            <h3 id="save-view-title">Save current view</h3>
            <label>
              View name
              <input
                autoFocus
                value={viewName}
                onChange={(e) => setViewName(e.target.value)}
                placeholder="e.g. Kerfoot pending"
              />
            </label>
            <div className="modal__actions">
              <button type="button" onClick={() => setSaveViewOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                disabled={!viewName.trim()}
                onClick={saveView}
              >
                Save view
              </button>
            </div>
          </div>
        </div>
      )}
      <DashboardPanels
        overview={overview}
        mapPoints={mapPoints}
        clients={clients}
        filters={filters}
        update={update}
        applyNumber={applyNumber}
        loading={loading}
      />
      <AnalyticsStrip analytics={overview?.analytics} />
      <section id="jobs-grid" className="overview-band">
        <div className="band-heading">
          <h3>Jobs</h3>
          {/* Both units, because both are real: a combined PV+battery site is
              ONE visit to schedule and ONE ServiceM8 work order, but TWO
              inspections to report and invoice. */}
          <span>
            {overview
              ? `${overview.totalVisits.toLocaleString()} ${overview.totalVisits === 1 ? 'visit' : 'visits'} · ${overview.totalJobs.toLocaleString()} inspections${overview.totalVisits > visits.length ? ` · showing first ${visits.length}` : ''}`
              : 'Loading…'}
          </span>
        </div>
        {/* Corrections for work orders that already exist. Kerfoot keep editing
            their sheet after we raise the job, so a booking note an inspector
            reads goes stale; this is what carries the change into ServiceM8.
            Always a preview first — a rewrite is an operator decision. */}
        {pushableJobIds.length > 0 && (
          <div className="geocode-prompt">
            <span>
              {pushBusy === 'preview'
                ? `Checking ServiceM8… ${pushProgress ? `${pushProgress.done} of ${pushProgress.total}` : ''}`
                : pushBusy === 'apply'
                  ? `Updating ServiceM8… ${pushProgress ? `${pushProgress.done} of ${pushProgress.total}` : ''}`
                  : pushResult
                    ? pushResult.applied
                      ? `Updated ${pushResult.updates.length} ServiceM8 work order${pushResult.updates.length === 1 ? '' : 's'}.`
                      : pushResult.updates.length === 0
                        ? 'Every ServiceM8 work order on screen is already up to date.'
                        : `${pushResult.updates.length} of ${pushableJobIds.length} work order${pushableJobIds.length === 1 ? '' : 's'} would change${preservingNotes > 0 ? `, ${preservingNotes} keeping notes somebody else wrote` : ''}.`
                    : `${pushableJobIds.length} job${pushableJobIds.length === 1 ? '' : 's'} on screen ${pushableJobIds.length === 1 ? 'has' : 'have'} a ServiceM8 work order. Check whether any booking notes are out of date.`}
            </span>
            {pushBusy === null && (!pushResult || pushResult.applied) && (
              <button type="button" className="dark-btn" onClick={() => void runPushUpdates(false)}>
                Check ServiceM8 for changes
              </button>
            )}
            {pushBusy === null &&
              pushResult &&
              !pushResult.applied &&
              pushResult.updates.length > 0 && (
                <button
                  type="button"
                  className="dark-btn"
                  onClick={() => void runPushUpdates(true)}
                >
                  Apply {pushResult.updates.length} update
                  {pushResult.updates.length === 1 ? '' : 's'}
                </button>
              )}
          </div>
        )}
        {pushError && <p className="banner banner--error">{pushError}</p>}
        {pushResult && pushResult.skipped.length > 0 && (
          <p className="banner banner--warning">
            {pushResult.skipped.length} skipped —{' '}
            {pushResult.skipped
              .slice(0, 3)
              .map((entry) => `${entry.jobId}: ${entry.reason}`)
              .join('; ')}
            {pushResult.skipped.length > 3 && ' …'}
          </p>
        )}
        {/* The diff itself. Open by default before applying, because agreeing
            to it is the whole point of the preview; collapsed afterwards, when
            it has become a receipt. */}
        {pushResult && pushResult.updates.length > 0 && (
          <details className="sm8-preview" open={!pushResult.applied}>
            <summary>
              {pushResult.applied ? 'What was written' : 'What would change'} (
              {pushResult.updates.length})
            </summary>
            {pushResult.updates.map((update) => (
              <div key={update.jobId} className="sm8-preview__job">
                <strong>
                  {update.jobId}
                  {update.serviceM8JobNumber ? ` → ServiceM8 #${update.serviceM8JobNumber}` : ''}
                </strong>
                {update.changes.map((change) => (
                  <div key={change.field} className="sm8-preview__field">
                    <span className="muted">{change.field}</span>
                    <pre className="sm8-preview__before">{change.from || '—'}</pre>
                    <pre className="sm8-preview__after">{change.to}</pre>
                  </div>
                ))}
              </div>
            ))}
          </details>
        )}
        {loading && !overview ? (
          <p className="loading">Loading overview…</p>
        ) : (
          <div className="overview-jobs-table">
            {/* Visit is what ServiceM8 holds — the purchase order for the whole
                trip. Code names which inspection a row is, so it only earns its
                ink on a combined visit; a single-inspection visit leaves it
                blank rather than repeating itself. */}
            <div className="overview-jobs-row overview-jobs-row--header">
              <div>Visit</div>
              <div>Code</div>
              <div>Client</div>
              <div>Site</div>
              <div>Type</div>
              <div>kW</div>
              <div>Status</div>
              <div>Inspector</div>
            </div>
            {visits.map((visit) => (
              <VisitRow
                key={visit.visitId}
                visit={visit}
                clients={clients}
                search={filters.search}
                open={openVisits.has(visit.visitId)}
                onToggle={() => toggleVisit(visit.visitId)}
              />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

/** Tiny debounce hook — same as Reports, no extra deps. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function findTrend(overview: JobsOverview | null, metric: Trend['metric']): Trend | undefined {
  return overview?.analytics.trends.find((trend) => trend.metric === metric);
}

/** What one inspection is: the unit both rows of a disclosed visit are in. */
function inspectionTypeLabel(job: InspectionJob): string {
  return job.inspectionType === 'pv' ? 'PVD' : 'BTY';
}

/**
 * PVD / BTY / PVD+BTY for a whole visit. For panels that cannot disclose the
 * second half — Recent Jobs — where collapsing to just the PV code would hide
 * that the visit carries a battery inspection at all.
 */
function visitTypeLabel(visit: JobVisit): string {
  if (visit.partner) return 'PVD+BTY';
  return inspectionTypeLabel(visit.primary);
}

/**
 * Feed address, with Clinton's geolocate hint when the stored geocoder label
 * names a different street or locality. The feed text is not rewritten.
 */
function SiteAddress({ job }: { job: InspectionJob }) {
  const hint = job.address.line1
    ? geolocatedAddressHint(job.address, job.location?.matchedAddress)
    : undefined;
  const line = job.address.line1 || job.address.suburb;
  return (
    <>
      {line}
      {hint ? (
        <>
          {' '}
          <span className="address-geolocate-hint">({hint})</span>
        </>
      ) : null}
      {`, ${job.address.state}`}
    </>
  );
}

/**
 * One site trip, listed as its PV inspection with the battery half disclosed
 * underneath — the visit's own code and type on the parent, the partner's on
 * the child, so a row means one inspection wherever you read it and the
 * Code(s) and Type columns always agree.
 *
 * The PV half leads because that is what ServiceM8 raises the work order from;
 * the codes recombine into `"PVD… / BTY…"` for the purchase order, which is
 * how the calendar and the import's ServiceM8 preview name the same visit.
 *
 * The disclosure sits outside the row's `Link` rather than inside it: a button
 * nested in an anchor is invalid, and the row has to stay a real link so it can
 * be middle-clicked into a new tab like every other row on the dashboard.
 */
function VisitRow({
  visit,
  clients,
  search,
  open,
  onToggle,
}: {
  visit: JobVisit;
  clients: Client[];
  /** The live search. A query that names one half opens that job. */
  search: string;
  open: boolean;
  onToggle: () => void;
}) {
  const job = visit.primary;
  const openJob = visitOpenJob(visit, search);
  const partner = visit.partner;
  const site = `${job.address.line1 || job.address.suburb}, ${job.address.state}`;
  const clientName =
    clients.find((client) => client.clientId === job.clientId)?.shortName ?? job.clientId;
  return (
    <div className={`overview-jobs-visit ${open ? 'overview-jobs-visit--open' : ''}`}>
      {partner && (
        <button
          type="button"
          className="visit-toggle"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} the second inspection at ${site}`}
          onClick={onToggle}
        >
          <Icon name="caret" />
        </button>
      )}
      <Link className="overview-jobs-row" to={`/jobs/${encodeURIComponent(openJob.jobId)}`}>
        <div className="mono">{visit.purchaseOrderNumber}</div>
        <div className="mono">{partner ? job.jobId : ''}</div>
        <div>{clientName}</div>
        <div className="overview-jobs-site">
          <SiteAddress job={job} />
        </div>
        <div>{inspectionTypeLabel(job)}</div>
        <div>{job.systemSizeKw ?? '—'}</div>
        <div>
          <i className={`job-pill job-pill--${job.status}`}>{jobStatusLabel(job.status)}</i>
        </div>
        <div>{job.inspectorId ?? '—'}</div>
      </Link>
      {open && partner && (
        <Link
          className="overview-jobs-row overview-jobs-row--half"
          to={`/jobs/${encodeURIComponent(partner.jobId)}`}
        >
          {/* The visit is named once, on the row above. */}
          <div className="visit-child-marker" aria-hidden="true">
            ↳
          </div>
          <div className="mono">{partner.jobId}</div>
          <div />
          <div />
          <div>{inspectionTypeLabel(partner)}</div>
          <div>{partner.systemSizeKw ?? '—'}</div>
          <div>
            <i className={`job-pill job-pill--${partner.status}`}>
              {jobStatusLabel(partner.status)}
            </i>
          </div>
          <div>{partner.inspectorId ?? '—'}</div>
        </Link>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Cockpit panels
 * ──────────────────────────────────────────────────────────────────────────── */

function DashboardPanels({
  overview,
  mapPoints,
  clients,
  filters,
  update,
  applyNumber,
  loading,
}: {
  overview: JobsOverview | null;
  mapPoints: { points: JobMapPoint[]; ungeocoded: number };
  clients: Client[];
  filters: Filters;
  update: (patch: Partial<Filters>) => void;
  applyNumber: (clientId: string, rollup: Filters['rollup'], type?: Filters['type']) => void;
  loading: boolean;
}) {
  const navigate = useNavigate();
  // Grouped before slicing, or a combo spent two of the eight slots saying the
  // same thing about one site.
  const recent = groupJobsIntoVisits(
    [...(overview?.jobs ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
  ).slice(0, 8);
  const missingInspector = overview?.jobs.filter(
    (job) => !job.inspectorId && job.status !== 'cancelled' && job.status !== 'closed',
  ).length;
  const worstClient = [...(overview?.clients ?? [])].sort(
    (a, b) => b.pending.total - a.pending.total,
  )[0];
  const over30 = overview?.clients.reduce((sum, row) => sum + row.pendingOver30, 0);
  const bookTotal = overview?.clients.reduce((sum, row) => sum + row.allocated.total, 0) ?? 0;
  return (
    <>
      <section className="dashboard-cockpit" aria-label="Operational snapshot">
        <div className="overview-band action-panel">
          <div className="band-heading">
            <h3>Action Required Today</h3>
          </div>
          <div className="action-panel__rows">
            <ActionRow
              icon="alert-circle"
              tone="danger"
              label={worstClient?.clientName ?? 'Pending reports'}
              detail={
                worstClient
                  ? `${worstClient.pending.total.toLocaleString()} pending reports`
                  : `${overview?.kpis.pendingSubmission.toLocaleString() ?? '—'} pending`
              }
              detailTone="danger"
              onClick={() =>
                worstClient
                  ? applyNumber(worstClient.clientId, 'pending')
                  : update({ rollup: 'pending' })
              }
            />
            <ActionRow
              icon="clock"
              tone="warning"
              label={over30 === undefined ? '—' : `${over30.toLocaleString()} reports`}
              detail="older than 30 days"
              detailTone="warning"
              onClick={() => update({ rollup: 'pending' })}
            />
            <ActionRow
              icon="flag"
              tone="danger"
              label={
                overview
                  ? `${overview.kpis.atRisk} ${overview.kpis.atRisk === 1 ? 'milestone' : 'milestones'}`
                  : '—'
              }
              detail="at risk"
              detailTone="danger"
              onClick={() =>
                document.getElementById('milestones')?.scrollIntoView({ behavior: 'smooth' })
              }
            />
            <ActionRow
              icon="users"
              tone="warning"
              label={missingInspector === undefined ? '—' : `${missingInspector} jobs`}
              detail="missing inspector"
              detailTone="warning"
              onClick={() =>
                document.getElementById('jobs-grid')?.scrollIntoView({ behavior: 'smooth' })
              }
            />
          </div>
          <button
            type="button"
            className="panel-link"
            onClick={() =>
              document.getElementById('jobs-grid')?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            View all actions
          </button>
        </div>
        <div id="milestones" className="overview-band milestone-panel">
          <div className="band-heading">
            <h3>Milestone Progress</h3>
            <div className="panel-legend">
              <span>
                <i className="dot dot--green" />
                On Track
              </span>
              <span>
                <i className="dot dot--red" />
                At Risk
              </span>
            </div>
          </div>
          <div className="milestone-panel__rows">
            {overview?.milestones.slice(0, 6).map((item) => (
              <button
                type="button"
                key={item.trancheId}
                onClick={() => update({ clientId: item.clientId, trancheId: item.trancheId })}
              >
                <span className="milestone-panel__name">
                  <strong>{item.clientName}</strong> - {item.trancheName}
                </span>
                <i className="milestone-bar">
                  <b style={{ width: `${Math.min(100, item.progressPercent)}%` }} />
                </i>
                <strong className="milestone-panel__pct">
                  {Math.round(item.progressPercent)}%
                </strong>
                <em className={item.atRisk ? 'pill-flag pill-flag--risk' : 'pill-flag'}>
                  {item.atRisk ? 'At Risk' : 'On Track'}
                </em>
                <Icon name="chevron" />
              </button>
            ))}
          </div>
          <button
            type="button"
            className="panel-link"
            onClick={() =>
              document.getElementById('client-summary')?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            View full report
          </button>
        </div>
        <JobsMap
          clusters={overview?.mapClusters ?? []}
          jobs={overview?.jobs ?? []}
          points={mapPoints.points}
          ungeocoded={mapPoints.ungeocoded}
          loading={loading && !overview}
          selectedState={filters.state}
          onSelectState={(state) => update({ state: filters.state === state ? '' : state })}
          onSelectJob={(jobId) => void navigate(`/jobs/${encodeURIComponent(jobId)}`)}
        />
      </section>
      <section className="dashboard-detail-row">
        <div id="client-summary" className="overview-band performance-panel">
          <div className="band-heading">
            <h3>Client Performance Summary</h3>
            <button
              type="button"
              className="panel-link panel-link--inline"
              onClick={() =>
                document.getElementById('jobs-grid')?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              View full report
            </button>
          </div>
          <div className="performance-table">
            <div className="performance-row performance-row--group">
              <span />
              <span />
              <span />
              <span className="group-head" style={{ gridColumn: 'span 2' }}>
                Allocated
              </span>
              <span className="group-head" style={{ gridColumn: 'span 2' }}>
                Submitted
              </span>
              <span className="group-head" style={{ gridColumn: 'span 3' }}>
                Pending Submission
              </span>
            </div>
            <div className="performance-row performance-row--head">
              <span>Client</span>
              <span>Status</span>
              <span>Total Jobs</span>
              <span>#</span>
              <span>%</span>
              <span>#</span>
              <span>%</span>
              <span>#</span>
              <span>%</span>
              <span>&gt;30 days</span>
            </div>
            {overview?.clients.map((row) => {
              const total = row.allocated.total;
              const pct = (value: number) => (total ? Math.round((value / total) * 100) : 0);
              // Every allocated job belongs to the client, so "% of own total"
              // would always read 100. Show the client's share of the whole
              // book instead, which is the number that actually varies.
              const sharePct = bookTotal ? Math.round((total / bookTotal) * 100) : 0;
              return (
                <div className="performance-row" key={row.clientId}>
                  <strong className="performance-row__client">
                    <i className="client-glyph">{row.clientName.slice(0, 1)}</i>
                    {row.clientName}
                  </strong>
                  <span className={`client-status client-status--${row.status}`}>
                    {row.status === 'on-track'
                      ? 'On Track'
                      : row.status === 'at-risk'
                        ? 'At Risk'
                        : 'Attention'}
                  </span>
                  <span className="performance-row__total">{total.toLocaleString()}</span>
                  <button
                    type="button"
                    className="cell cell--neutral"
                    onClick={() => applyNumber(row.clientId, 'allocated')}
                  >
                    {row.allocated.total.toLocaleString()}
                  </button>
                  <span className="cell cell--neutral">{sharePct}%</span>
                  <button
                    type="button"
                    className="cell cell--good"
                    onClick={() => applyNumber(row.clientId, 'submitted')}
                  >
                    {row.submitted.total.toLocaleString()}
                  </button>
                  <span className="cell cell--good">{pct(row.submitted.total)}%</span>
                  <button
                    type="button"
                    className={`cell ${row.status === 'at-risk' ? 'cell--risk' : 'cell--warn'}`}
                    onClick={() => applyNumber(row.clientId, 'pending')}
                  >
                    {row.pending.total.toLocaleString()}
                  </button>
                  <span
                    className={`cell ${row.status === 'at-risk' ? 'cell--risk' : 'cell--warn'}`}
                  >
                    {pct(row.pending.total)}%
                  </span>
                  <span className={`cell ${row.pendingOver30 ? 'cell--risk' : ''}`}>
                    {row.pendingOver30}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="overview-band recent-panel">
          <div className="band-heading">
            <h3>Recent Jobs</h3>
            <button
              type="button"
              className="panel-link panel-link--inline"
              onClick={() =>
                document.getElementById('jobs-grid')?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              View all jobs
            </button>
          </div>
          <div className="recent-jobs">
            {/* "Visit", not "Job ID": this panel has no disclosure, so a row is
                the whole trip and carries the combined purchase order and a
                PVD+BTY type. Splitting Visit from Code the way the jobs grid
                does would buy nothing here — there is no second row for the
                Code column to distinguish. */}
            <div className="recent-jobs__head">
              <span>Visit</span>
              <span>Client</span>
              <span>Site</span>
              <span>Type</span>
              <span>kW</span>
              <span>Status</span>
              <span>Inspector</span>
              <span>Submitted</span>
              <span>Approved</span>
            </div>
            {recent.map((visit) => {
              const job = visit.primary;
              const openJob = visitOpenJob(visit, filters.search);
              return (
                <Link
                  key={visit.visitId}
                  to={`/jobs/${encodeURIComponent(openJob.jobId)}`}
                  className="recent-jobs__row"
                >
                  <span className="mono">{visit.purchaseOrderNumber}</span>
                  <span>
                    {clients.find((client) => client.clientId === job.clientId)?.shortName ??
                      job.clientId}
                  </span>
                  <span className="recent-jobs__site">
                    <SiteAddress job={job} />
                  </span>
                  <span>{visitTypeLabel(visit)}</span>
                  <span>{job.systemSizeKw ?? '—'}</span>
                  <span>
                    <i className={`job-pill job-pill--${job.status}`}>
                      {jobStatusLabel(job.status)}
                    </i>
                  </span>
                  <span>{job.inspectorId ?? '–'}</span>
                  <span className="recent-jobs__updated">{formatStamp(job.submittedAt)}</span>
                  <span className="recent-jobs__updated">{formatStamp(job.approvedAt)}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

/** `dd/mm/yyyy h:mma` — matches the density of the recent-jobs clocks. */
function formatStamp(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })} ${date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

function ActionRow({
  icon,
  tone,
  label,
  detail,
  detailTone,
  onClick,
}: {
  icon: IconName;
  tone: 'danger' | 'warning';
  label: string;
  detail: string;
  detailTone: 'danger' | 'warning';
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}>
      <i className={`action-dot action-dot--${tone}`}>
        <Icon name={icon} />
      </i>
      <span className="action-row__label">{label}</span>
      <span className={`action-row__detail action-row__detail--${detailTone}`}>{detail}</span>
      <Icon name="chevron" />
    </button>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Thirty-day analytics
 * ──────────────────────────────────────────────────────────────────────────── */

function AnalyticsStrip({ analytics }: { analytics: JobsOverview['analytics'] | undefined }) {
  const types = analytics?.types;
  const total = types?.total ?? 0;
  const segments = [
    { key: 'bty', label: 'BTY', value: types?.bty ?? 0, colour: '#2563eb' },
    { key: 'pvd', label: 'PVD', value: types?.pvd ?? 0, colour: '#38bdf8' },
    { key: 'both', label: 'PVD-BTY', value: types?.both ?? 0, colour: '#a5b4fc' },
  ];
  return (
    <section className="analytics-strip" aria-label="Thirty day performance and job mix">
      {(analytics?.trends ?? []).map((trend) => (
        <TrendCard key={trend.metric} trend={trend} />
      ))}
      <div className="trend-card type-card">
        <span className="trend-card__title">Jobs by Type</span>
        <div className="type-card__body">
          <TypeDonut segments={segments} total={total} />
          <ul>
            {segments.map((segment) => (
              <li key={segment.key}>
                <i className="type-key" style={{ background: segment.colour }} />
                <span>{segment.label}</span>
                <b>
                  {total ? Math.round((segment.value / total) * 100) : 0}% (
                  {segment.value.toLocaleString()})
                </b>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function TypeDonut({
  segments,
  total,
}: {
  segments: { key: string; value: number; colour: string }[];
  total: number;
}) {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="type-donut">
      <svg viewBox="0 0 120 120" role="img" aria-label="Jobs by inspection type">
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#e8edf5" strokeWidth="16" />
        {total > 0 &&
          segments.map((segment) => {
            const length = (segment.value / total) * circumference;
            const dash = `${length} ${circumference - length}`;
            const element = (
              <circle
                key={segment.key}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={segment.colour}
                strokeWidth="16"
                strokeDasharray={dash}
                strokeDashoffset={-offset}
                transform="rotate(-90 60 60)"
              />
            );
            offset += length;
            return element;
          })}
      </svg>
      <div className="type-donut__centre">
        <strong>{total.toLocaleString()}</strong>
        <small>Total Jobs</small>
      </div>
    </div>
  );
}

function TrendCard({ trend }: { trend: Trend }) {
  const labels: Record<Trend['metric'], string> = {
    allocated: 'Allocations',
    booked: 'Bookings',
    submitted: 'Submissions',
    completed: 'Completed Reports',
  };
  const delta = trendDelta(trend);
  const max = Math.max(1, ...trend.points.map((point) => point.count));
  const step = trend.points.length > 1 ? 220 / (trend.points.length - 1) : 220;
  const coords = trend.points.map((point, index) => ({
    x: index * step,
    y: 60 - (point.count / max) * 52,
  }));
  const line = coords.map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} 220,60 0,60`;
  return (
    <div className={`trend-card trend-card--${trend.metric}`}>
      <span className="trend-card__title">
        {labels[trend.metric]} <small>(Last 30 Days)</small>
      </span>
      <strong className="trend-card__value">{trend.total.toLocaleString()}</strong>
      {delta ? (
        <span className={`trend-delta ${delta.up ? 'trend-delta--up' : 'trend-delta--down'}`}>
          <Icon name={delta.up ? 'up' : 'down'} />
          {delta.percent}%<small>vs prior 15 days</small>
        </span>
      ) : (
        <span className="trend-delta trend-delta--none">
          <small>Insufficient history</small>
        </span>
      )}
      {trend.sufficient && (
        <svg
          className="trend-card__spark"
          viewBox="0 0 220 60"
          preserveAspectRatio="none"
          role="img"
          aria-label={`${labels[trend.metric]} daily trend`}
        >
          <polygon points={area} fill="currentColor" opacity=".12" />
          <polyline
            points={line}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Job Locations map
 * ──────────────────────────────────────────────────────────────────────────── */

/** Which legend bucket a job falls into. Mirrors the cluster roll-ups. */
function pinTone(job: InspectionJob): 'allocated' | 'scheduled' | 'complete' {
  if (job.status === 'uploaded' || job.status === 'closed') return 'complete';
  if (job.scheduledDate && job.status !== 'cancelled') return 'scheduled';
  return 'allocated';
}

const MAINLAND_PATH = toPath(MAINLAND, true);
const TASMANIA_PATH = toPath(TASMANIA, true);
const BORDER_PATHS = BORDERS.map((border) => toPath(border, false));
const ZOOM_STEPS = [1, 1.6, 2.5, 4, 6];

function JobsMap({
  clusters,
  jobs,
  points,
  ungeocoded,
  loading,
  selectedState,
  onSelectState,
  onSelectJob,
}: {
  clusters: JobsOverview['mapClusters'];
  jobs: readonly InspectionJob[];
  points: JobMapPoint[];
  ungeocoded: number;
  loading: boolean;
  selectedState: string;
  onSelectState: (state: string) => void;
  onSelectJob: (jobId: string) => void;
}) {
  // Start on the interactive map; drop to the schematic if it can't load.
  const [interactive, setInteractive] = useState(true);
  const total = clusters.reduce((sum, cluster) => sum + cluster.count, 0);
  const [baseMapUrl, setBaseMapUrl] = useState<string>();
  const [zoomIndex, setZoomIndex] = useState(0);
  const [centre, setCentre] = useState({ x: MAP_FRAME.width / 2, y: MAP_FRAME.height / 2 });
  const [expanded, setExpanded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);

  // Markers are specified in screen pixels, so we need the rendered size of the
  // canvas to convert them into the frame's user units.
  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect;
      if (box) setCanvasSize({ width: box.width, height: box.height });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    let objectUrl: string | undefined;
    void fetchJobsMapBaseUrl()
      .then((url) => {
        objectUrl = url;
        if (active) setBaseMapUrl(url);
        else URL.revokeObjectURL(url);
      })
      .catch(() => {
        // The static basemap only backs the schematic fallback; the
        // interactive map reports its own availability. Stay quiet here.
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  // Escape leaves the expanded view.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const zoom = ZOOM_STEPS[zoomIndex] ?? 1;
  const viewWidth = MAP_FRAME.width / zoom;
  const viewHeight = MAP_FRAME.height / zoom;
  const clamp = (value: number, size: number, extent: number) =>
    Math.max(size / 2, Math.min(extent - size / 2, value));
  const cx = clamp(centre.x, viewWidth, MAP_FRAME.width);
  const cy = clamp(centre.y, viewHeight, MAP_FRAME.height);
  const viewBox = `${cx - viewWidth / 2} ${cy - viewHeight / 2} ${viewWidth} ${viewHeight}`;
  // User units per rendered pixel, under `xMidYMid meet`. Markers scale by this
  // so a 19px badge stays 19px at every zoom level and panel size.
  const pixelScale =
    canvasSize.width && canvasSize.height
      ? 1 / Math.min(canvasSize.width / viewWidth, canvasSize.height / viewHeight)
      : 1;

  // Schematic-fallback pins, one per visit — a combo is one rooftop, and two
  // pins stacked on it made the fallback disagree with both the cluster badge
  // above it and the interactive map it stands in for.
  const pins = useMemo(
    () =>
      groupJobsIntoVisits(jobs)
        .map((visit) => (visit.primary.location ? visit.primary : visit.partner))
        .filter(
          (job): job is InspectionJob & { location: NonNullable<InspectionJob['location']> } =>
            Boolean(job?.location),
        )
        .slice(0, 400),
    [jobs],
  );

  /**
   * Cluster badges sit on state capitals, and Sydney/Canberra/Melbourne are
   * close enough at this scale to bury each other. Nudge overlapping badges
   * apart (largest placed first, so the big numbers keep their true position)
   * and flip the city label to whichever side has room.
   */
  const placedClusters = useMemo(() => {
    const items = [...clusters]
      .sort((a, b) => b.count - a.count)
      .map((cluster) => {
        const { x, y } = projectToFrame(cluster.longitude, cluster.latitude);
        return {
          cluster,
          x,
          y,
          radius: Math.max(17, Math.min(28, 14 + Math.sqrt(cluster.count) * 0.95)),
        };
      });
    for (let pass = 0; pass < 30; pass += 1) {
      let moved = false;
      for (let i = 0; i < items.length; i += 1) {
        for (let j = i + 1; j < items.length; j += 1) {
          const a = items[i];
          const b = items[j];
          if (!a || !b) continue;
          const minimum = (a.radius + b.radius + 16) * pixelScale;
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          let distance = Math.hypot(dx, dy);
          if (distance === 0) {
            dx = 0;
            dy = 1;
            distance = 1;
          }
          if (distance >= minimum) continue;
          const push = (minimum - distance) / 2;
          a.x -= (dx / distance) * push;
          a.y -= (dy / distance) * push;
          b.x += (dx / distance) * push;
          b.y += (dy / distance) * push;
          moved = true;
        }
      }
      if (!moved) break;
    }
    return items;
  }, [clusters, pixelScale]);

  function onPointerDown(event: React.PointerEvent<SVGSVGElement>) {
    if (zoom === 1) return;
    dragRef.current = { x: event.clientX, y: event.clientY, cx, cy };
    event.currentTarget.setPointerCapture(event.pointerId);
  }
  function onPointerMove(event: React.PointerEvent<SVGSVGElement>) {
    const drag = dragRef.current;
    if (!drag) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const dx = ((event.clientX - drag.x) / rect.width) * viewWidth;
    const dy = ((event.clientY - drag.y) / rect.height) * viewHeight;
    setCentre({ x: drag.cx - dx, y: drag.cy - dy });
  }
  function endDrag() {
    dragRef.current = null;
  }

  return (
    <section
      className={`jobs-map overview-band ${expanded ? 'jobs-map--expanded' : ''}`}
      aria-labelledby="job-locations-title"
    >
      <div className="band-heading">
        <h3 id="job-locations-title">Job Locations</h3>
        <span>{loading ? 'Loading…' : `${total.toLocaleString()} visits`}</span>
      </div>
      <div className="jobs-map__canvas" ref={canvasRef}>
        {interactive && (
          <JobLocationsMap
            points={points}
            onSelectJob={onSelectJob}
            onUnavailable={() => setInteractive(false)}
          />
        )}
        {!interactive && (
          <svg
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Job locations across Australia"
            className={zoom > 1 ? 'is-pannable' : ''}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <defs>
              <linearGradient id="map-water" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#eaf4fd" />
                <stop offset="1" stopColor="#dbeafe" />
              </linearGradient>
            </defs>
            <rect
              x="0"
              y="0"
              width={MAP_FRAME.width}
              height={MAP_FRAME.height}
              fill="url(#map-water)"
            />
            {baseMapUrl ? (
              <image
                href={baseMapUrl}
                x="0"
                y="0"
                width={MAP_FRAME.width}
                height={MAP_FRAME.height}
                preserveAspectRatio="none"
              />
            ) : (
              <g className="jobs-map__schematic">
                <path className="jobs-map__land" d={MAINLAND_PATH} />
                <path className="jobs-map__land" d={TASMANIA_PATH} />
                {BORDER_PATHS.map((d, index) => (
                  <path className="jobs-map__border" key={index} d={d} />
                ))}
                <g className="jobs-map__state-labels" aria-hidden="true">
                  {STATE_LABELS.map((entry) => {
                    const { x, y } = projectToFrame(entry.lon, entry.lat);
                    return (
                      <text key={entry.label} x={x} y={y} style={{ fontSize: 11 * pixelScale }}>
                        {entry.label}
                      </text>
                    );
                  })}
                </g>
              </g>
            )}
            <g className="jobs-map__pins">
              {pins.map((job) => {
                const { x, y } = projectToFrame(job.location.longitude, job.location.latitude);
                return (
                  <path
                    key={job.jobId}
                    className={`jobs-map__pin jobs-map__pin--${pinTone(job)}`}
                    transform={`translate(${x} ${y}) scale(${pixelScale})`}
                    d="M0 0c-4.6-6.6-7.4-10.2-7.4-13.4a7.4 7.4 0 1 1 14.8 0C7.4-10.2 4.6-6.6 0 0z"
                  >
                    <title>
                      {job.jobId} — {job.address.suburb}, {job.address.state}
                    </title>
                  </path>
                );
              })}
            </g>
            {placedClusters.map(({ cluster, x, y, radius }) => {
              const active = selectedState === cluster.state;
              const city = STATE_CITY[cluster.state] ?? cluster.state;
              return (
                <g
                  key={`${cluster.state}:${cluster.latitude}:${cluster.longitude}`}
                  className={`jobs-map__cluster ${active ? 'jobs-map__cluster--active' : ''}`}
                  transform={`translate(${x} ${y}) scale(${pixelScale})`}
                  role="button"
                  tabIndex={0}
                  aria-label={`${cluster.state}: ${cluster.count} jobs; ${cluster.scheduled} scheduled; ${cluster.complete} submitted${cluster.approximate ? '; positioned on the state capital' : ''}`}
                  onClick={() => onSelectState(cluster.state)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') onSelectState(cluster.state);
                  }}
                >
                  <circle r={radius} />
                  <text className="jobs-map__count" y="1">
                    {cluster.count.toLocaleString()}
                  </text>
                  {/* Under the badge, not beside it: the south-east capitals sit
                    close enough that side labels overlap the next cluster. */}
                  <text className="jobs-map__city" y={radius + 12} textAnchor="middle">
                    {city}
                  </text>
                </g>
              );
            })}
          </svg>
        )}
        <div className="jobs-map__legend">
          <span>
            <i className="dot dot--allocated" />
            Allocated
          </span>
          <span>
            <i className="dot dot--scheduled" />
            Scheduled
          </span>
          <span>
            <i className="dot dot--complete" />
            Inspection complete
          </span>
        </div>
        {!interactive && (
          <div className="jobs-map__zoom">
            <button
              type="button"
              aria-label="Zoom in"
              disabled={zoomIndex >= ZOOM_STEPS.length - 1}
              onClick={() => setZoomIndex((index) => Math.min(ZOOM_STEPS.length - 1, index + 1))}
            >
              <Icon name="plus" />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              disabled={zoomIndex === 0}
              onClick={() => {
                setZoomIndex((index) => Math.max(0, index - 1));
                if (zoomIndex - 1 <= 0)
                  setCentre({ x: MAP_FRAME.width / 2, y: MAP_FRAME.height / 2 });
              }}
            >
              <Icon name="minus" />
            </button>
            <button
              type="button"
              aria-label={expanded ? 'Exit full screen map' : 'Expand map'}
              aria-pressed={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              <Icon name="expand" />
            </button>
          </div>
        )}
        {!loading && clusters.length === 0 && (
          <p className="jobs-map__empty">No mapped jobs match these filters.</p>
        )}
      </div>
      <div className="jobs-map__foot">
        <small>
          {!interactive
            ? 'Interactive map unavailable — showing schematic outline.'
            : ungeocoded > 0
              ? `Map © Amazon Location Service · ${ungeocoded.toLocaleString()} not yet geocoded`
              : 'Map © Amazon Location Service'}
        </small>
        <button
          type="button"
          className="panel-link panel-link--inline"
          onClick={() => onSelectState('')}
        >
          {selectedState ? 'Clear state filter' : 'Select a cluster to filter'}
        </button>
      </div>
    </section>
  );
}
